import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./council.css";
import Tab from "../tab/tab";
import DS from "../../../assets/img/DS.png";
import report from "../../../assets/img/report.png";
import { publicApiClient } from "../../../api/apiClient";

const INTRO_API = "/api/council/intro";
const PAST_API = "/api/council/past?page=0&size=100";
const ORG_API = "/api/council/org/executives";

const getArrayData = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.orgUnits)) return data.orgUnits;
  if (Array.isArray(data?.units)) return data.units;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const getMemberArray = (item) => {
  if (Array.isArray(item?.members)) return item.members;
  if (Array.isArray(item?.orgMembers)) return item.orgMembers;
  if (Array.isArray(item?.orgMemberList)) return item.orgMemberList;
  if (Array.isArray(item?.memberList)) return item.memberList;

  return [];
};

const normalizePastCouncil = (item) => ({
  id: item.pastCouncilId || item.id,
  generation: item.termNumber || "",
  name: item.councilName || "",
  councilType: item.councilType || "STUDENT_COUNCIL",
  president: item.president || "",
  vicePresident: item.vicePresident || "",
  link: item.reportUrl || "",
});

const normalizeOrgMember = (member) => ({
  id:
    member.orgMemberId ||
    member.org_member_id ||
    member.orgId ||
    member.id ||
    null,
  name:
    member.orgMemberName ||
    member.org_member_name ||
    member.memberName ||
    member.name ||
    member.userName ||
    "",
  role:
    member.role ||
    member.position ||
    member.positionName ||
    member.roleName ||
    member.orgMemberRole ||
    member.org_member_role ||
    member.duty ||
    member.title ||
    "",
});

const normalizeOrgUnit = (item) => ({
  id: item.orgUnitId || item.org_unit_id || item.id,
  unitName: item.unitName || item.unit_name || item.name || "",
  headName:
    item.unitHeadName ||
    item.unit_head_name ||
    item.headName ||
    item.president ||
    item.presidentName ||
    "",
  viceHeadName:
    item.viceHeadName ||
    item.vice_head_name ||
    item.vicePresident ||
    item.vicePresidentName ||
    item.viceUnitHeadName ||
    item.vice_unit_head_name ||
    "",
  headStudentId: item.unitHeadStudentId || item.unit_head_student_id || "",
  members: getMemberArray(item).map(normalizeOrgMember),
});

const isPresidentRole = (role = "") => {
  return (
    (role.includes("총학생회장") || role === "회장" || role.includes("회장")) &&
    !role.includes("부")
  );
};

const isVicePresidentRole = (role = "") => {
  return (
    role.includes("부총학생회장") ||
    role.includes("부회장") ||
    role.includes("부총") ||
    role.includes("부")
  );
};

const formatExecutiveOrgData = (result) => {
  const rawData = getArrayData(result);

  if (rawData.length === 0) return [];

  const hasUnitStructure = rawData.some(
    (item) =>
      item.unitName ||
      item.unit_name ||
      item.orgUnitId ||
      item.org_unit_id ||
      Array.isArray(item.members) ||
      Array.isArray(item.orgMembers) ||
      Array.isArray(item.memberList)
  );

  if (hasUnitStructure) {
    return rawData.map(normalizeOrgUnit);
  }

  const members = rawData.map(normalizeOrgMember);

  const presidentMember = members.find((member) =>
    isPresidentRole(member.role)
  );

  const vicePresidentMember = members.find((member) =>
    isVicePresidentRole(member.role)
  );

  return [
    {
      id: "executives",
      unitName: "회장단",
      headName: presidentMember?.name || members[0]?.name || "",
      viceHeadName:
        vicePresidentMember?.name ||
        members.find((member) => member.name !== presidentMember?.name)?.name ||
        members[1]?.name ||
        "",
      headStudentId: "",
      members,
    },
  ];
};

const normalizeTermNumber = (value) => {
  if (!value) return "";
  return String(value).replace(/[^0-9]/g, "");
};

const normalizeCouncilStatus = (introData) => {
  const rawStatus =
    introData?.status ||
    introData?.councilStatus ||
    introData?.councilType ||
    introData?.type ||
    "STUDENT_COUNCIL";

  const status = String(rawStatus).toUpperCase();

  if (
    status.includes("EMERGENCY") ||
    status.includes("EMERGENCY_COMMITTEE") ||
    status.includes("비상") ||
    status.includes("비대위") ||
    status.includes("비상대책")
  ) {
    return "EMERGENCY";
  }

  if (
    status.includes("ACTING") ||
    status.includes("권한") ||
    status.includes("대행")
  ) {
    return "ACTING";
  }

  return "STUDENT_COUNCIL";
};

const getGreetingTitleText = ({ status, currentTermNumber, councilName }) => {
  const termText = currentTermNumber ? `제${currentTermNumber}대` : "";

  if (status === "EMERGENCY") {
    return `${termText ? `${termText} ` : ""}비상대책위원회`;
  }

  if (status === "ACTING") {
    return "현재 권한대행 상태입니다.";
  }

  const nameText = councilName ? ` ${councilName}` : "";

  return `${termText ? `${termText}` : ""}${nameText} 총학생회장단`;
};

const getGreetingMemberText = ({
  status,
  presidentName,
  vicePresidentName,
}) => {
  if (status === "EMERGENCY") {
    return "";
  }

  if (status === "ACTING") {
    return "";
  }

  return [presidentName, vicePresidentName].filter(Boolean).join(" · ");
};

const renderTextWithLineBreak = (text) => {
  if (!text) return null;

  const lines = String(text).split("\n");

  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index !== lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

export default function Council() {
  const [activeTab, setActiveTab] = useState("intro");

  const [introData, setIntroData] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [orgUnitList, setOrgUnitList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const tabs = [
    { key: "intro", label: "총학생회" },
    { key: "greeting", label: "인사말" },
    { key: "history", label: "역대 총학생회" },
  ];

  const fetchCouncilData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const [introResult, pastResult] = await Promise.all([
        publicApiClient(INTRO_API),
        publicApiClient(PAST_API),
      ]);

      setIntroData(introResult?.data || null);

      const pastData = getArrayData(pastResult)
        .map(normalizePastCouncil)
        .filter((item) => item.councilType === "STUDENT_COUNCIL")
        .sort(
          (a, b) =>
            Number(normalizeTermNumber(b.generation)) -
            Number(normalizeTermNumber(a.generation))
        );

      setHistoryList(pastData);

      try {
        const orgResult = await publicApiClient(ORG_API);
        const orgData = formatExecutiveOrgData(orgResult);

        console.log("회장단 조회 성공:", orgData);

        setOrgUnitList(orgData);
      } catch (orgError) {
        console.warn("회장단 정보 조회 실패:", orgError);
        setOrgUnitList([]);
      }
    } catch (error) {
      console.error("총학생회 정보 조회 오류:", error);
      setErrorMsg(error.message || "총학생회 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCouncilData();
  }, [fetchCouncilData]);

  const chairmanUnit = useMemo(() => {
    return orgUnitList.find((unit) => unit.unitName?.includes("회장단"));
  }, [orgUnitList]);

  const presidentName = useMemo(() => {
    const introPresident =
      introData?.president ||
      introData?.presidentName ||
      introData?.chairman ||
      introData?.chairmanName ||
      introData?.leaderName ||
      "";

    if (introPresident) return introPresident;

    if (!chairmanUnit) return "";

    const presidentMember = chairmanUnit.members?.find((member) =>
      isPresidentRole(member.role)
    );

    return chairmanUnit.headName || presidentMember?.name || "";
  }, [introData, chairmanUnit]);

  const vicePresidentName = useMemo(() => {
    const introVicePresident =
      introData?.vicePresident ||
      introData?.vicePresidentName ||
      introData?.viceChairman ||
      introData?.viceChairmanName ||
      introData?.subLeaderName ||
      "";

    if (introVicePresident) return introVicePresident;

    if (!chairmanUnit) return "";

    const vicePresidentMember = chairmanUnit.members?.find((member) =>
      isVicePresidentRole(member.role)
    );

    return (
      chairmanUnit.viceHeadName ||
      vicePresidentMember?.name ||
      chairmanUnit.members?.find((member) => member.name !== presidentName)
        ?.name ||
      ""
    );
  }, [introData, chairmanUnit, presidentName]);

  const currentTermNumber = normalizeTermNumber(introData?.termNumber);
  const councilStatus = normalizeCouncilStatus(introData);

  const greetingTitleText = getGreetingTitleText({
    status: councilStatus,
    currentTermNumber,
    councilName: introData?.name || "",
  });

  const greetingMemberText = getGreetingMemberText({
    status: councilStatus,
    presidentName,
    vicePresidentName,
  });

  const hasGreetingData =
    introData?.mainImgUrl ||
    introData?.slogan ||
    introData?.intro ||
    greetingTitleText ||
    greetingMemberText;

  if (loading) {
    return (
      <div className="council-page">
        <Tab />

        <div className="section-card intro-card">
          <p>총학생회 정보를 불러오는 중입니다.</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="council-page">
        <Tab />

        <div className="section-card intro-card">
          <p>{errorMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="council-page">
      <Tab />

      <div className="sub-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? "active" : ""}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "intro" && (
        <div className="section-card intro-card">
          <img src={DS} alt="덕성 로고" className="intro-logo" />

          <p>
            덕성여자대학교 총학생회는
            &lt;덕성여자대학교 총학생회 회칙 제1장 제2조&#40;목적&#41;&gt;에 따라
            민주적이고 자율적인 학생회 활동을 통해 진보적이고 참여적인 학문 탐구 및
            대학 문화 창달을 위해 활동하는 것을 목적으로 학생들을 위해 존재하는 학생
            자치 기구입니다.
          </p>

          <p>
            덕성여자대학교는 독립운동가 차미리사 선생이 3.1운동 정신을 이어받아 순수
            민족 자본으로 세운 최초의 여성교육기관으로 덕성&#40;德性&#41;을 갖춘
            창의적 지식인 육성을 목적으로 한다. 2000년 우리 덕성인은 스스로 덕성의
            뿌리를 되찾기 위해 노력하고 덕성 민주화를 위해 투쟁해 왔으며 2018년에는
            총장 직선제를 이루어내는 역사를 펼쳐왔다. 이러한 역사를 바탕으로
            덕성여자대학교 총학생회는 본교 창학이념인 ‘자립·자각·자생’ 정신에 따라
            자주적인 학문 탐구와 끝없는 덕성 민주화를 위해 덕성의 역사를 이어갈 의무가
            있다. 이에 모든 덕성인은 일치단결하여 어떤 불의와 탄압 속에서도 여성운동가
            차미리사 선생의 정신으로 창의성과 전문성을 겸비한 여성 인재로서 사명을
            다할 것을 천명한다.
          </p>

          <p className="rule-caption">
            _ ⌜덕성여자대학교 총학생회칙⌟ 전문 중 일부 발췌
          </p>
        </div>
      )}

      {activeTab === "greeting" && (
        <div className="section-card greeting-card">
          {hasGreetingData ? (
            <div className="greeting-img">
              {introData?.mainImgUrl && (
                <img
                  src={introData.mainImgUrl}
                  alt="총학생회 로고"
                  className="greeting-logo"
                />
              )}

              <div className="greeting-text">
                {introData?.slogan && <h3>{introData.slogan}</h3>}

                {introData?.intro && (
                  <p className="slogan">
                    {renderTextWithLineBreak(introData.intro)}
                  </p>
                )}

                {greetingTitleText && (
                  <p className="council">
                    {councilStatus === "ACTING"
                      ? greetingTitleText
                      : `덕성여자대학교 ${greetingTitleText}`}

                    {greetingMemberText && (
                      <>
                        <br />
                        {greetingMemberText}
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p>등록된 인사말 정보가 없습니다.</p>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <div className="history-list">
          {historyList.length > 0 ? (
            historyList.map((item) => (
              <div
                className="section-card history-card"
                key={item.id || item.generation}
              >
                {item.link && (
                  <a
                    href={item.link}
                    className="history-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={report} alt="활동 보고" className="history-icon" />
                  </a>
                )}

                <h3>
                  제{normalizeTermNumber(item.generation)}대 총학생회{" "}
                  {item.name}
                </h3>

                <p>
                  총학생회장 | {item.president || "-"} · 부총학생회장 |{" "}
                  {item.vicePresident || "-"}
                </p>
              </div>
            ))
          ) : (
            <div className="section-card history-card">
              <p>등록된 역대 총학생회 정보가 없습니다.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}